import { useState, useEffect } from 'react';
import { formatReportMoney, idleLimit, resolveIdleGraceMinutes } from '@evi/shared';
import { Tag, Plus, Pencil, Trash2 } from 'lucide-react';
import { fetchTariffs, deleteTariff, type Tariff } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { TariffForm } from '@/components/ui/TariffForm';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './TariffsPage.module.css';

const TYPE_LABELS: Record<string, string> = {
  flat: 'Flat',
  time_of_use: 'Time of Use',
  tiered: 'Tiered',
};

export function TariffsPage() {
  useRecentRoute('Tariffs');
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tariff | undefined>();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setTariffs(await fetchTariffs());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tariffs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onEdit = (t: Tariff) => {
    setEditing(t);
    setModalOpen(true);
  };

  const onCreate = () => {
    setEditing(undefined);
    setModalOpen(true);
  };

  const onDelete = async (t: Tariff) => {
    if (!confirm(`Delete tariff '${t.name}'?`)) return;
    try {
      await deleteTariff(t._id);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Tag size={18} /></div>
          <div>
            <h1 className={styles.title}>Tariff Plans</h1>
            <p className={styles.subtitle}>{tariffs.length} active tariff{tariffs.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={onCreate}>
          <Plus size={16} /> Add Tariff
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading...</p>
      ) : (
        <TariffsResponsiveTable
          tariffs={tariffs}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Tariff' : 'New Tariff'}
        size="lg"
      >
        <TariffForm
          existing={editing}
          onSuccess={() => { setModalOpen(false); load(); }}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

function TariffsResponsiveTable({
  tariffs, onEdit, onDelete,
}: {
  tariffs: Tariff[];
  onEdit: (t: Tariff) => void;
  onDelete: (t: Tariff) => void;
}) {
  const columns: Column<Tariff>[] = [
    {
      key: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: (t) => (
        <span className={styles.name}>
          {t.name}
          {t.is_default && (
            <span className={styles.defaultBadge} style={{ marginLeft: 8 }}>Default</span>
          )}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      mobile: 'secondary',
      cell: (t) => (
        <span className={styles.typeBadge}>{TYPE_LABELS[t.type] ?? t.type}</span>
      ),
    },
    {
      key: 'rate',
      header: 'Energy Rate',
      mobile: 'body',
      align: 'right',
      cell: (t) => (
        <span className={styles.mono}>{t.rates.energy_rate.toFixed(2)}¢/kWh</span>
      ),
    },
    {
      key: 'idle-rate',
      header: 'Idle rate',
      mobile: 'body',
      cell: (t) => t.rate_type !== 'per_hour' && (t.rates.time_rate ?? 0) > 0
        ? <strong>{formatReportMoney(t.rates.time_rate, t.currency)}/min</strong> : 'No idle fee',
    },
    {
      key: 'idle-grace', header: 'Idle grace', mobile: 'body',
      cell: (t) => t.rate_type !== 'per_hour' && (t.rates.time_rate ?? 0) > 0
        ? <strong>{resolveIdleGraceMinutes(undefined, t.rates.idle_grace_minutes)} min</strong> : 'Not applicable',
    },
    {
      key: 'idle-cap', header: 'Effective idle cap', mobile: 'body',
      cell: (t) => {
        if (t.rate_type === 'per_hour' || !(t.rates.time_rate! > 0)) return 'Not applicable';
        const cap = idleLimit(t.rates.time_rate!, t.rates.idle_max_charge_cents);
        return <span><strong>{formatReportMoney(cap.maximum, t.currency)}</strong><br />
          <strong>{cap.hostMaximum ? `${cap.minutes} billed min` : '120 billed min'}</strong>{cap.hostMaximum ? ' (host maximum)' : ''}</span>;
      },
    },
    {
      key: 'tax',
      header: 'Tax',
      mobile: 'body',
      align: 'right',
      cell: (t) => <span className={styles.mono}>{(t.tax_rate * 100).toFixed(2)}%</span>,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (t) => (t.is_active ? 'Active' : 'Inactive'),
    },
    {
      key: 'actions',
      header: '',
      mobile: 'body',
      align: 'right',
      cell: (t) => (
        <div className={styles.actions}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(t); }}
            title="Edit"
            aria-label={`Edit ${t.name}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(t); }}
            title="Delete"
            className={styles.deleteBtn}
            aria-label={`Delete ${t.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={tariffs}
      rowKey={(t) => t._id}
      emptyMessage={'No tariffs yet — click "Add Tariff" to get started'}
    />
  );
}
